import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { ApiError } from '@/lib/api'
import { billingKeys } from '@/lib/queryKeys'
import { fetchMonthEndUsage } from '../api/api'
import type { MonthEndUsageResponse } from '../types'

export function useMonthEndUsageQuery(period: string, enabled: boolean) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery<MonthEndUsageResponse | null>({
    queryKey: billingKeys.monthEndUsage(organization?.id ?? '', period),
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error('No auth token available')
      try {
        return await fetchMonthEndUsage(token, period, signal)
      } catch (e) {
        // A 404 here means the period exists in the org's history but the
        // monthly close job hasn't written the immutable `monthly_twilio_adjustment`
        // ledger row yet (typically days 1-2 of the next month, during
        // Twilio's revision grace window). Surface this as `null` so the
        // UI can render a friendly "period closing" state instead of an
        // error. Any other failure propagates as a real error.
        if (e instanceof ApiError && e.status === 404) {
          return null
        }
        throw e
      }
    },
    enabled: enabled && !!organization?.id,
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
